// ---------------------------------------------------------------------------
// PropertiesStep — Shared property questions for dso_properties,
// retailer_properties, and authorities_conditions
// ---------------------------------------------------------------------------
// Options grouped by metadata.property_key. Each group = one question section.
// Within each group, single-select radio buttons. Next enabled when all
// groups answered. Payload format differs by stepId.
// ---------------------------------------------------------------------------

import { memo, useCallback, useMemo, useState } from 'react';
import { StepHeading } from '@/components/shared/StepHeading';
import { FooterActions } from '@/components/shell/FooterActions';
import { t } from '@/i18n';
import { getStepIcon } from '@/utils/stepIcons';
import type { StepId, StepOption } from '@/api/types';
import { useCollectedData } from '@/hooks/useCollectedData';
import type { StepProps } from '@/types/steps';
import styles from './PropertiesStep.module.css';

// ---------------------------------------------------------------------------
// Heading config per step
// ---------------------------------------------------------------------------

const DSO_HEADING = {
  title: 'steps.dso_properties.title',
  subtitle: 'steps.dso_properties.subtitle',
};

const HEADING_KEYS: Record<string, { title: string; subtitle: string }> = {
  dso_properties: DSO_HEADING,
  retailer_properties: {
    title: 'steps.retailer_properties.title',
    subtitle: 'steps.retailer_properties.subtitle',
  },
  authorities_conditions: {
    title: 'steps.authorities_conditions.title',
    subtitle: 'steps.authorities_conditions.subtitle',
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface PropertyGroup {
  propertyKey: string;
  question: string;
  options: StepOption[];
}

/** Group options by metadata.property_key, preserving order. */
function groupByPropertyKey(options: StepOption[]): PropertyGroup[] {
  const map = new Map<string, PropertyGroup>();

  for (const opt of options) {
    const meta = opt.metadata;
    const key = typeof meta['property_key'] === 'string' ? meta['property_key'] : 'unknown';
    const question = typeof meta['question'] === 'string' ? meta['question'] : key;

    let group = map.get(key);
    if (!group) {
      group = { propertyKey: key, question, options: [] };
      map.set(key, group);
    }
    group.options.push(opt);
  }

  return [...map.values()];
}

/** Build initial selections from options where metadata.is_default === true. */
function buildDefaults(groups: PropertyGroup[]): Record<string, string> {
  const defaults: Record<string, string> = {};
  for (const group of groups) {
    for (const opt of group.options) {
      const meta = opt.metadata;
      if (meta['is_default'] === true) {
        defaults[group.propertyKey] = opt.id;
        break;
      }
    }
  }
  return defaults;
}

/** Does this step submit condition_value strings or ltp_id numbers? */
function isAuthoritiesConditions(stepId: StepId): boolean {
  return stepId === 'authorities_conditions';
}

// ---------------------------------------------------------------------------
// PropertiesStep
// ---------------------------------------------------------------------------

export const PropertiesStep = memo(function PropertiesStep({
  stepId,
  options,
  onSubmit,
  isSubmitting,
}: StepProps) {
  const groups = useMemo(() => groupByPropertyKey(options.options), [options.options]);

  const collectedData = useCollectedData();

  const [selected, setSelected] = useState<Record<string, string>>(() => {
    // Try to restore from collected_data (back-navigation pre-fill)
    const group = stepId === 'dso_properties' ? 'dso' : stepId === 'retailer_properties' ? 'retailer' : null;
    if (group) {
      const nested = collectedData[group];
      if (typeof nested === 'object' && nested !== null) {
        const propVals = (nested as Record<string, unknown>)['property_values'];
        if (typeof propVals === 'object' && propVals !== null) {
          const vals = propVals as Record<string, unknown>;
          const restored: Record<string, string> = {};
          for (const g of groups) {
            const cv = vals[g.propertyKey];
            if (typeof cv === 'string') {
              // Map condition_value back to option id
              const match = g.options.find((o) => o.metadata['condition_value'] === cv);
              if (match) {
                restored[g.propertyKey] = match.id;
                continue;
              }
            }
            // Fall back to is_default
            for (const opt of g.options) {
              if (opt.metadata['is_default'] === true) {
                restored[g.propertyKey] = opt.id;
                break;
              }
            }
          }
          if (Object.keys(restored).length > 0) return restored;
        }
      }
    }
    return buildDefaults(groups);
  });

  const allAnswered = useMemo(
    () => groups.length > 0 && groups.every((g) => selected[g.propertyKey] !== undefined),
    [groups, selected],
  );

  const handleSelect = useCallback((propertyKey: string, optionId: string) => {
    setSelected((prev) => ({ ...prev, [propertyKey]: optionId }));
  }, []);

  const handleSubmit = useCallback(() => {
    if (!allAnswered) return;

    if (isAuthoritiesConditions(stepId)) {
      // authorities_conditions: keys = property_key, values = selected option id (prv_ prefixed)
      const data: Record<string, string> = {};
      for (const group of groups) {
        const optionId = selected[group.propertyKey];
        if (optionId !== undefined) {
          data[group.propertyKey] = optionId;
        }
      }
      onSubmit(data);
    } else {
      // dso_properties / retailer_properties: keys = property_key, values = condition_value (string)
      const data: Record<string, string> = {};
      for (const group of groups) {
        const optionId = selected[group.propertyKey];
        if (optionId === undefined) continue;
        const opt = group.options.find((o) => o.id === optionId);
        const meta = opt?.metadata;
        const conditionValue =
          typeof meta?.['condition_value'] === 'string' ? meta['condition_value'] : optionId;
        data[group.propertyKey] = conditionValue;
      }
      onSubmit(data);
    }
  }, [allAnswered, stepId, groups, selected, onSubmit]);

  const heading = HEADING_KEYS[stepId] ?? DSO_HEADING;

  const headingTitle = heading.title;
  const headingSubtitle = heading.subtitle;

  return (
    <>
      <StepHeading title={t(headingTitle)} subtitle={t(headingSubtitle)} icon={getStepIcon(stepId)} />

      <div className={styles['groups']}>
        {groups.map((group) => (
          <fieldset key={group.propertyKey} className={styles['group']} disabled={isSubmitting}>
            <legend className={styles['question']}>{group.question}</legend>
            <div className={styles['options']} role="radiogroup" aria-label={group.question}>
              {group.options.map((opt) => {
                const isSelected = selected[group.propertyKey] === opt.id;
                return (
                  <label key={opt.id} className={styles['option']}>
                    <input
                      type="radio"
                      className={styles['radio']}
                      name={`prop-${group.propertyKey}`}
                      value={opt.id}
                      checked={isSelected}
                      onChange={() => handleSelect(group.propertyKey, opt.id)}
                    />
                    <span className={`${styles['card']} ${isSelected ? styles['selected'] : ''}`}>
                      <span className={styles['name']}>{opt.name}</span>
                      {opt.description && <span className={styles['desc']}>{opt.description}</span>}
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        ))}
      </div>

      <FooterActions
        primaryLabel={t('common.next')}
        onPrimary={handleSubmit}
        isLoading={isSubmitting}
        disabled={!allAnswered}
      />
    </>
  );
});
